import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Avatar,
  Button,
  EmptyState,
  GlassCard,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@jumpstone/ui-react';
import { JobTab } from './profile_tabs/job_tab';
import { PayrollTab } from './profile_tabs/payroll_tab';
import { BankTab } from './profile_tabs/bank_tax_tabs';
import { AccessTab } from './profile_tabs/access_tab';
import { useEmployee, useEmployeeRecord } from '../../data/use_employees';
import { useEmployeeMutations } from '../../data/use_employee_mutations';
import { useSettings } from '../../data/use_settings';
import { useTermination } from '../../data/use_termination';
import {
  useEmployeeCompensation,
  type UseEmployeeCompensationResult,
} from '../../data/use_employee_compensation';
import { deriveJurisdiction } from './employee_form_validation';
import { EmployeeFormDialog } from './employee_form_dialog';
import { ChangeSalaryDialog } from './change_salary_dialog';
import { todayIsoUtc } from './employee_form_compensation_utils';
import { currentEffectiveSince } from './profile_tabs/salary_history_utils';
import { EmploymentStatusBadge } from './employment_status_badge';
import { EmploymentTypeBadge } from './employment_type_badge';
import { is_contractor } from '../../lib/worker_types';
import {
  TerminateConfirmDialog,
  type TerminationDraft,
} from './terminate_confirm_dialog';
import { FinalPayDrawer } from './final_pay_drawer';
import { localise_fixture } from '../../data/mock_labels';
import { EMPLOYEE_PROFILE_TABS, navigate } from '../../lib/router';
import type { EmployeeProfileTab } from '../../lib/router';
import type { Employee } from '../../data/types';

const TAB_LABEL_KEY: Record<EmployeeProfileTab, string> = {
  job: 'employees.tab_job',
  payroll: 'employees.tab_payroll',
  bank: 'employees.tab_bank',
  access: 'employees.tab_access',
};

/**
 * One employee's full record, split across the eight profile tabs. Wired to
 * the real `payroll.listEmployees` directory (C2, #22326) via
 * `useEmployee()` — a real self-fetching hook, not a store lookup.
 *
 * `is_loading` MUST be checked before `!employee` below — `useEmployee()`
 * returns `employee: null` for BOTH "still fetching" and "genuinely not
 * found", and rendering the not-found `EmptyState` during the loading
 * window would flash an incorrect "not found" message on every profile
 * navigation before the real fetch resolves.
 *
 * An unmatched `employee_id` (a stale bookmarked URL, a deleted employee, a
 * hostile/guessed id) renders this not-found `EmptyState` — `useEmployee()`
 * no longer falls back to a different employee's record for an unknown id
 * (see that hook's own docblock for why the old `.at(0)` fallback was a
 * real privacy/IDOR-shaped bug against real data, not a cosmetic nicety).
 *
 * `entitlement_required`/`error` get their OWN copy, distinct from the
 * not-found `EmptyState` above (PR #22369 adversarial review finding #4) —
 * before this fix, `useEmployee()` returned `employee: null` for those
 * states too, so a missing-`payroll`-add-on FORBIDDEN or a real fetch
 * failure both misleadingly rendered "Employee not found" instead of a
 * message that actually describes what went wrong. `no_entity` is NOT
 * given separate copy here — `AuthGate` (`main.tsx`) already gates a
 * `no_entity` `useSessionFacts()` result before the app shell mounts, so it
 * is unreachable from this screen in practice; it falls through to the
 * not-found copy defensively, same as the directory screen's handling.
 */
export function EmployeeProfileScreen({
  employee_id,
  tab,
}: {
  employee_id: string | null;
  tab: EmployeeProfileTab;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { employee, is_loading, state, retry } = useEmployee(employee_id);
  const {
    record,
    retry: retry_record,
    pay_currencies,
  } = useEmployeeRecord(employee_id);
  const { update, is_read_only_grant } = useEmployeeMutations();
  const termination_actions = useTermination();
  // ONE compensation instance for the whole screen: the header button, the
  // edit form's summary, the Change salary dialog and the Payroll tab's
  // history card all read/refresh the same fetch.
  const compensation = useEmployeeCompensation(employee_id);
  const { organisation } = useSettings();
  const jurisdiction = deriveJurisdiction(organisation.base_currency);
  const [edit_open, set_edit_open] = useState(false);
  const [salary_open, set_salary_open] = useState(false);
  const [terminate_confirm_open, set_terminate_confirm_open] = useState(false);
  const [final_pay_open, set_final_pay_open] = useState(false);
  const [termination_draft, set_termination_draft] =
    useState<TerminationDraft | null>(null);

  // `useEmployee`/`useEmployeeRecord` are independent fetch instances (each
  // hook call gets its own `useEffect`, per `use_employees.ts`'s established
  // "no cross-component cache" shape) -- a successful edit must refresh BOTH,
  // or reopening the edit dialog would show the pre-save raw record and a
  // second save would silently revert the first (adversarial review
  // finding).
  function handle_employee_saved(): void {
    retry();
    retry_record();
  }

  // The compensation hook refreshes its own history after a successful
  // schedule/cancel; the profile + raw record (which carry today's cached pay)
  // need their own refetch.
  function handle_salary_scheduled(): void {
    retry();
    retry_record();
  }

  function handle_termination_success(): void {
    set_termination_draft(null);
    retry();
    retry_record();
  }

  if (is_loading) {
    return (
      <GlassCard className="rounded-2xl p-5 mb-3.5">
        <span className="sr-only">{t('employees.profile_loading')}</span>
        <div className="nv-skel" style={{ height: 64, margin: 8 }} />
        <div className="nv-skel" style={{ height: 44, margin: 8 }} />
      </GlassCard>
    );
  }

  if (state.status === 'entitlement_required') {
    return (
      <EmptyState
        title={t('employees.profile_entitlement_required_title')}
        description={t('employees.profile_entitlement_required_description')}
      />
    );
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title={t('employees.profile_error_title')}
        description={t('employees.profile_error_description')}
        action={
          <Button variant="secondary" onClick={retry}>
            {t('employees.profile_retry')}
          </Button>
        }
      />
    );
  }

  if (!employee) {
    return (
      <EmptyState
        title={t('employees.not_found')}
        description={t('employees.not_found_description')}
      />
    );
  }

  return (
    <>
      <GlassCard className="rounded-2xl p-5 mb-3.5">
        <div className="flex items-center flex-wrap gap-3.5">
          <Avatar name={employee.name} colorVariant={employee.tint} size="lg" />
          <div className="min-w-0">
            <p className="font-bold text-[18px] truncate">{employee.name}</p>
            <p className="text-[13px] text-[color:var(--ink-muted)] mt-0.5">
              {t('employees.profile_summary', {
                role: localise_fixture(employee.job_title, locale),
                department: localise_fixture(employee.department, locale),
              })}
            </p>
          </div>
          <span className="ms-auto flex items-center gap-2.5">
            <EmploymentTypeBadge type={employee.employment_type} />
            <EmploymentStatusBadge status={employee.status} />
            {!is_read_only_grant && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  set_edit_open(true);
                }}
              >
                {t('common.edit')}
              </Button>
            )}
            {record !== null &&
              compensation.schedule_change !== undefined &&
              !is_contractor(employee.employment_type) && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    set_salary_open(true);
                  }}
                >
                  {t('employees.change_salary')}
                </Button>
              )}
            {!termination_actions.is_read_only_grant &&
              employee.status !== 'terminated' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    set_terminate_confirm_open(true);
                  }}
                  data-testid="employee-profile-terminate"
                >
                  {t('employees.terminate.action_button')}
                </Button>
              )}
          </span>
        </div>
      </GlassCard>

      <TerminateConfirmDialog
        open={terminate_confirm_open}
        onOpenChange={set_terminate_confirm_open}
        employee_id={employee.id}
        employee_name={employee.name}
        onOpenFinalPay={(draft) => {
          // The confirm dialog closes itself before calling this and passes
          // through the exact date + reason the operator picked; the
          // drawer's journal date and audit reason must match those values
          // (adversarial-review fix — never re-derive `today_iso()` here).
          set_termination_draft(draft);
          set_final_pay_open(true);
        }}
        terminate={termination_actions.terminate}
        onSuccess={handle_termination_success}
      />

      {termination_draft && (
        <FinalPayDrawer
          open={final_pay_open}
          onOpenChange={(next) => {
            set_final_pay_open(next);
            if (!next) set_termination_draft(null);
          }}
          jurisdiction={jurisdiction}
          is_contractor={is_contractor(employee.employment_type)}
          employee_id={employee.id}
          employee_name={employee.name}
          draft={termination_draft}
          actions={termination_actions}
          onSuccess={handle_termination_success}
        />
      )}

      {record && (
        <EmployeeFormDialog
          open={edit_open}
          onOpenChange={set_edit_open}
          mode="edit"
          initial={record}
          jurisdiction={jurisdiction}
          currency={organisation.base_currency}
          payCurrencies={pay_currencies}
          create={undefined}
          update={update}
          onSaved={handle_employee_saved}
          salarySince={currentEffectiveSince(compensation.rows, todayIsoUtc())}
        />
      )}

      {record && (
        <ChangeSalaryDialog
          open={salary_open}
          onOpenChange={set_salary_open}
          employee={record}
          currency={organisation.base_currency}
          payCurrencies={pay_currencies}
          schedule={compensation.schedule_change}
          onScheduled={handle_salary_scheduled}
        />
      )}

      <Tabs
        value={tab}
        onValueChange={(next) => {
          navigate({
            module: 'employees',
            view: 'profile',
            employee_id: employee.id,
            tab: next as EmployeeProfileTab,
          });
        }}
      >
        <TabsList className="wp-tabstrip mb-4">
          {EMPLOYEE_PROFILE_TABS.map((id) => (
            <TabsTrigger key={id} value={id}>
              {t(TAB_LABEL_KEY[id])}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <ProfileTabPanel
        tab={tab}
        employee={employee}
        compensation={compensation}
      />
    </>
  );
}

function ProfileTabPanel({
  tab,
  employee,
  compensation,
}: {
  tab: EmployeeProfileTab;
  employee: Employee;
  compensation: UseEmployeeCompensationResult;
}): React.JSX.Element {
  switch (tab) {
    case 'job':
      return <JobTab employee={employee} />;
    case 'payroll':
      return <PayrollTab employee={employee} compensation={compensation} />;
    case 'bank':
      return <BankTab employee={employee} />;
    case 'access':
      return <AccessTab employee={employee} />;
  }
}
